import Decimal from 'decimal.js'
import pino from 'pino'

import { StateError } from '@ankor-io/common/lang/backend/errors'

const logger = pino({ name: 'currency' })

/**
 * Convert a currency amount from one currency to another
 * @param amountCents - The amount in cents
 * @param rate - The conversion rate
 * @returns The converted amount in cents
 */
export const convert = (amountCents: number, rate: string): number => {
  // we need a robust implementation of this function, make sure the amount is a non-negative integer
  if (!Number.isInteger(amountCents)) {
    throw new Error('Amount must be an integer in cents')
  }
  // if the rate is not provided or not a number, then throw an error
  if (!rate || isNaN(parseFloat(rate))) {
    throw new Error('Rate must be a valid numeric string')
  }

  try {
    // Convert cents to decimal amount
    const amountDecimal = new Decimal(amountCents).div(100)

    // Perform conversion
    const convertedDecimal = amountDecimal.mul(rate)

    // Convert back to cents and round
    const convertedCents = convertedDecimal.mul(100).toDecimalPlaces(0, Decimal.ROUND_HALF_UP)

    const result = convertedCents.toNumber()

    // Check for overflow/underflow
    if (!Number.isFinite(result) || result > Number.MAX_SAFE_INTEGER) {
      throw new Error('Conversion result exceeds safe integer range')
    }

    return result
  } catch (error) {
    logger.error(error, 'Currency conversion failed')
    throw StateError('Currency conversion failed', { error })
  }
}

/**
 * Calculates VAT amount from a base amount
 * @param amountCents - Base amount in cents (integer)
 * @param vatPercentage - VAT percentage as string (e.g., "20", "7.5", "0")
 * @returns VAT amount in cents (integer)
 */
export const calculateVat = (amountCents: number, vatPercentage: string): number => {
  // Input validation
  if (!Number.isInteger(amountCents)) {
    throw new Error('Amount must be an integer in cents')
  }

  const vatPercent = new Decimal(vatPercentage)
  if (vatPercent.lt(0) || vatPercent.gt(100)) {
    throw new Error('VAT percentage must be between 0 and 100')
  }

  try {
    // Convert cents to decimal amount
    const amountDecimal = new Decimal(amountCents).div(100)

    // Calculate VAT: amount * (vatPercentage / 100)
    const vatAmount = amountDecimal.mul(vatPercent).div(100)

    // Convert back to cents and round
    const vatCents = vatAmount.mul(100).toDecimalPlaces(0, Decimal.ROUND_HALF_UP)

    return vatCents.toNumber()
  } catch (error) {
    logger.error(error, 'VAT calculation failed')
    throw StateError('VAT calculation failed', { error })
  }
}

export const sumAll = (amounts: bigint[]): bigint => {
  return amounts.reduce((acc, amount) => acc + amount, BigInt(0))
}

export const toCents = (amount: number): number => {
  if (isNaN(amount)) {
    throw new Error('Invalid amount')
  }
  return new Decimal(amount).mul(100).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber()
}

export const fromCents = (cents: number): number => {
  if (isNaN(cents)) {
    throw new Error('Invalid cents amount')
  }
  return new Decimal(cents).div(100).toNumber()
}

export const toNegative = (value: number) => new Decimal(value).abs().neg().toNumber()

export const toPositive = (value: number) => new Decimal(value).abs().toNumber()
